<script lang="ts" setup>
import Tooltip from '~/components/ui/tooltips/Tooltip.vue'
import PhotoIcon from '~/assets/icons/search/photo.svg?component'
import PromoIcon from '~/assets/icons/statuses/promotion.svg'
import MarkdownIcon from '~/assets/icons/statuses/markdown.svg'
import BasketIcon from '~/assets/icons/basket/24/delete.svg?component'
import ArrowIcon from '~/assets/icons/arrows/arrowDownBlack.svg'
import useSearchStore from '~/shared/store/modules/search'
import useMainStore from '~/shared/store'
import useAccountStore from '~/shared/store/modules/account'
import Badge from '~/components/ui/badge/Badge.vue'
import getFormatTitle from '~/composables/product-title-formatter'
import type {
  ISearchProduct,
  ISearchProductWarehouse,
} from '~/shared/interfaces/search/products'
import type { IImageSlide } from '~/shared/interfaces/ui/sliders'
import {
  onAccordionAfterEnter,
  onAccordionTransitionEnter,
  onAccordionTransitionLeave,
} from '~/components/ui/accordion/accordion-script'
import { useMonitorSize } from '~/composables/monitor-size'
import type { IMonitorSize } from '~/shared/interfaces/composables'
import AddToCartBtn from '~/features/search/AddToCartBtn.vue'
import AppButton from '~/components/ui/buttons/AppButton.vue'

const props = defineProps<{
  warehouse: ISearchProductWarehouse
  product: ISearchProduct
  isOriginalProduct: boolean
  showBaseProductInfo?: boolean
}>()

const mainStore = useMainStore()
const searchStore = useSearchStore()
const accountStore = useAccountStore()
const warehouseIsOpen = ref(false)
const isFooterColumnMobile = ref(false)

const sizes: IMonitorSize | null = useMonitorSize()

const shouldShowSupplierTooltip = computed(() => {
  return (
    props.warehouse.pricelist ||
    props.warehouse.address ||
    props.warehouse.workMode.length > 0 ||
    props.warehouse.probability
  )
})

const isShowSupplierInfo = computed(
  () => accountStore.userRole !== "guest" && accountStore.userRole !== "regular"
)

function toggleMobileRows() {
  warehouseIsOpen.value = !warehouseIsOpen.value
  isFooterColumnMobile.value = warehouseIsOpen.value
}

function handleButtonClickMobile() {
  isFooterColumnMobile.value = true
}

function showProductSlider(slides: IImageSlide[]) {
  searchStore.setSearchProductSlides(
    slides.map((slide) => ({
      ...slide,
      name: props.product.title,
    }))
  )
  mainStore.toggleFSSlider(true)
}
</script>

<template>
  <template v-if="!sizes.isMobile">
    <div class="search__table-row">
      <div class="search__table-cell">
        <template v-if="showBaseProductInfo">
          <div class="search__table-brand brand-name">
            {{ product.brand.name }}
          </div>
          <NuxtLink class="search__table-article" :to="product.searchLink">
            {{ product.article }}
          </NuxtLink>
        </template>
      </div>
      <div class="search__table-cell">
        <NuxtLink
          :to="warehouse.productLink"
          v-if="showBaseProductInfo"
          class="search__table-name tooltip-container"
          :class="!warehouse.productLink?.length && 'not-link'"
        >
          <span class="search__table-name-txt">{{
              getFormatTitle(product.title)
            }}</span>
          <Tooltip :text="getFormatTitle(product.title)" />
        </NuxtLink>
      </div>
      <div class="search__table-cell" :class="warehouseIsOpen && 'open'">
        <div
          class="search__table-cell-sub"
          :class="!warehouse.photos.length && 'disabled'"
        >
          <button
            class="search__table-slider-btn tooltip-container svg-fill-orange"
            v-if="warehouse.photos.length"
            @click="showProductSlider(warehouse.photos)"
          >
            <PhotoIcon />
            <Tooltip text="фото товара" />
          </button>
        </div>
        <div
          class="search__table-cell-sub"
          :class="
            !warehouse.isPromo &&
            !warehouse.isMarkdown &&
            !warehouse.isUsed &&
            'disabled'
          "
        >
          <div
            class="search__table-status tooltip-container"
            v-if="warehouse.isPromo"
          >
            <PromoIcon />
            <Tooltip text="акция" />
          </div>
          <Badge
            class="search__table-status"
            v-if="warehouse.isUsed"
            :tag="{ text: 'Б/у' }"
          />
          <div
            class="search__table-status tooltip-container"
            v-if="warehouse.isMarkdown"
          >
            <MarkdownIcon />
            <Tooltip text="уценка" />
          </div>
        </div>
        <div class="search__table-cell-sub">
          <div
            class="search__table-city"
            :class="warehouse.type"
          >
            <div class="search__table-city-name">{{ warehouse.city.name }}</div>
            <div class="search__table-city-warehouse">
              {{ warehouse.name }}
              <i class="search__table-probability" v-if="warehouse.probability">
                {{ warehouse.probability }}%
              </i>
            </div>
            <div class="search__table-city-arrow">
              <ArrowIcon />
            </div>
          </div>
        </div>
        <div class="search__table-cell-sub">
          <div class="search__table-warehouse tooltip-container">
            <p>склад {{ warehouse.name }}</p>
            <i v-if="warehouse.probability">{{ warehouse.probability }}%</i>
            <ClientOnly>
              <Tooltip v-if="shouldShowSupplierTooltip" size="l">
                <div
                  class="tooltip__item tooltip__item_warehouse search__table-tooltip"
                  v-if="
                    warehouse.pricelist?.supplier?.name && isShowSupplierInfo
                  "
                >
                  <span>Поставщик</span>
                  <span>{{ warehouse.pricelist?.supplier?.name }}</span>
                </div>
                <div
                  class="tooltip__item tooltip__item_warehouse search__table-tooltip"
                  v-if="warehouse.supplierDate && isShowSupplierInfo"
                >
                  <span>Обновлен</span>
                  <span>{{ warehouse.supplierDate }}</span>
                </div>
                <div
                  class="tooltip__item tooltip__item_warehouse search__table-tooltip"
                  v-if="warehouse.probability"
                >
                  <span>Вероятность поставки</span>
                  <span>{{ warehouse.probability }} %</span>
                </div>
                <div
                  class="tooltip__item tooltip__item_warehouse search__table-tooltip"
                  v-if="warehouse.address"
                >
                  <span>Адрес</span> <span>{{ warehouse.address }}</span>
                </div>
                <div
                  class="tooltip__item tooltip__item_warehouse search__table-tooltip search__table-tooltip_warehouses"
                  v-if="warehouse.workMode.length"
                >
                  <span>Режим работы </span>
                  <span v-html="warehouse.workModeHtml"></span>
                </div>
              </Tooltip>
            </ClientOnly>
          </div>
        </div>
        <div class="search__table-cell-sub">
          <div class="search__table-delivery">
            {{ warehouse.deliveryDateText }}
          </div>
        </div>
        <div class="search__table-cell-sub">
          <div
            class="search__table-quantity tooltip-container"
            v-if="warehouse.quantityWithReserve || warehouse.reserve"
          >
            {{
              (warehouse.quantity ?? 0) > 10
                ? ">10 шт."
                : `${warehouse.quantity} шт.`
            }}
            <Tooltip>
              <div class="tooltip__item search__table-tooltip">
                <span>свободно: </span>
                <span>{{ warehouse.quantityWithReserve }}</span>
              </div>
              <div class="tooltip__item search__table-tooltip">
                <span>в резерве: </span>
                <span>{{ warehouse.reserve }}</span>
              </div>
            </Tooltip>
          </div>
          <div class="search__table-quantity tooltip-container" v-else>
            ???
            <Tooltip> Уточните наличие у менеджера </Tooltip>
          </div>
        </div>
        <div class="search__table-cell-sub">
          <div
            class="search__table-price tooltip-container"
            v-if="warehouse.price"
          >
            {{ warehouse.price }}&nbsp;₽
            <Tooltip
              v-if="
                warehouse.pricelist &&
                isShowSupplierInfo &&
                warehouse.purchasePrice
              "
            >
              цена закупки: {{ warehouse.purchasePrice }}
            </Tooltip>
          </div>
          <div class="search__table-price tooltip-container" v-else>
            ???
            <Tooltip> Уточните цену у менеджера </Tooltip>
          </div>
        </div>
        <div class="search__table-cell-sub">
          <div class="search__table-add">
            <AddToCartBtn
              :warehouse="warehouse"
              :product="product"
              :is-original-product="isOriginalProduct"
            />
          </div>
        </div>
      </div>
    </div>
  </template>

  <template v-else>
    <div class="card" :class="warehouse.type">
      <div class="card__header" @click="toggleMobileRows">
        <div class="card__header-content">
          <span class="muted">Цена:&nbsp;</span>{{ warehouse.price }}&nbsp;₽
          <ArrowIcon
            class="card__header-icon"
            :class="warehouseIsOpen && 'card__header-icon_active'"
          />
        </div>
      </div>
      <Transition
        name="accordion-item"
        @enter="onAccordionTransitionEnter"
        @leave="onAccordionTransitionLeave"
        @after-enter="onAccordionAfterEnter"
      >
        <div v-show="warehouseIsOpen" class="card__content">
          <div class="card__content-inner-wrapper">
            <div class="card__icons">
              <AppButton
                v-if="warehouse.photos.length"
                class="card__slider-btn"
                type="icon"
                size="s"
                :icon="PhotoIcon"
                @click="showProductSlider(warehouse.photos)"
              />

              <div class="card__icons_status">
                <PromoIcon v-if="warehouse.isPromo" class="card__icon" />
                <MarkdownIcon v-if="warehouse.isMarkdown" class="card__icon" />
                <Badge
                  v-if="warehouse.isUsed"
                  class="card__badge"
                  :tag="{ text: 'Б/у', tagColor: 'dark-gray' }"
                />
              </div>
            </div>

            <p v-if="warehouse.deliveryDateText">
              <span class="muted">Срок поставки:</span>
              {{ warehouse.deliveryDateText }}
            </p>
            <p v-if="warehouse.quantityWithReserve || warehouse.reserve">
              <span class="muted">Количество:</span>
              {{
                (warehouse.quantity ?? 0) > 10
                  ? ">10 шт."
                  : `${warehouse.quantity} шт.`
              }}
            </p>
          </div>
        </div>
      </Transition>

      <div class="card__footer">
        <div
          class="card__footer-content"
          :class="isFooterColumnMobile && 'card__footer-content_column'"
        >
          <div class="card__city">
            <div class="card__city-name">{{ warehouse.city.name }}</div>
            <div class="card__city-warehouse">
              {{ warehouse.name }}

              <i class="card__city-probability" v-if="warehouse.probability">
                {{ warehouse.probability }}%
              </i>
            </div>
          </div>
          <div class="card__add-to-cart">
            <AddToCartBtn
              :warehouse="warehouse"
              :product="product"
              :is-original-product="isOriginalProduct"
              :is-input-shown="warehouseIsOpen"
              @buy-button-clicked-on-mobile="handleButtonClickMobile"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.card {
  margin-top: 16px;
  background-color: $bg_neutral;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.3s;

  .muted {
    color: $content_secondary;
  }

  &__header {
    cursor: pointer;
    padding: 20px 20px 12px;

    &-content {
      position: relative;
      display: flex;
      justify-content: space-between;
    }

    &-icon {
      margin-left: auto;
      transition: transform 0.3s;

      &_active {
        transform: rotate(180deg);
      }
    }
  }

  &__content {
    &-inner-wrapper {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 0 20px 12px;
    }
  }

  &__footer {
    padding: 0 20px 20px;
    background-color: $bg_neutral;

    &-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px 12px;
      padding-top: 12px;
      border-top: 1px solid #dbdbdb;
      position: relative;

      &::after {
        position: absolute;
        top: 0;
        left: 0;
        content: "";
        width: 100%;
        left: 50%;
        transform: translateX(-50%);
        height: 1px;
        background-color: $bg_border;
      }

      &_column{
        @include xs-breakpoint{
          flex-direction: column;
          align-items: flex-start;

          .card__add-to-cart{
            width: 100%;

            :deep(.counter),
            :deep(.counter-input) {
              width: 100%;
            }
            :deep(.counter-btn){
              flex-shrink: 0;
            }
          }
        }
      }
    }
  }

  &__city {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-shrink: 0;

    &-name {
      font-weight: 500;
    }

    &-warehouse {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }

    &-probability {
      @include font(12px, 16px, 500);
      padding: 2px 6px;
      border-radius: 10px;
      background-color: $bg_page;
    }
  }

  &__slider-btn {
    background-color: $bg_light-grey;
  }

  &__icons {
    display: flex;
    gap: 16px;
    align-items: center;

    &_status {
      display: flex;
      align-items: center;
    }
  }

  &__icon {
    width: 24px;
    height: 24px;
  }

  &__badge {
    padding: 2px 8px;
    @include font(14px, 20px, 500);
  }

  &__add-to-cart {
    display: flex;
    gap: 8px;
  }

  &.our_from_user_city {
    border-color: $content_success;
  }

  &.our_from_other_city {
    border-color: $content_warning;
  }

  &.partner {
    border-color: $content_tretiary;
  }
}
.tooltip {
  &__item {
    display: flex;
    gap: 8px;

    &_warehouse {
      &:not(:first-child) {
        margin-top: 8px !important;
      }

      span {
        &:first-child {
          color: $content_secondary;
        }

        &:last-child {
          :deep(b) {
            font-weight: 400;
          }
        }
      }
    }

    &:not(:first-child) {
      margin-top: 4px;
    }
  }
}
</style>
